import React, { useEffect, useRef, useState } from 'react';
import { Music, Volume2, VolumeX, Pause, Play } from 'lucide-react';
import { useData } from '../context/DataContext';

/**
 * Site-wide background music player.
 * - Reads settings.musicEnabled / musicUrl / musicTitle / musicAutoplay / musicVolume.
 * - Autoplays MUTED on load (browser policy compliant).
 * - Bell button toggles mute so the first click starts real audible playback.
 * - Persists user's "muted" choice in localStorage so it doesn't nag on every page.
 */
export default function MusicPlayer() {
  const { settings } = useData();
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    const v = typeof window !== 'undefined' ? localStorage.getItem('nv_music_muted') : null;
    return v === null ? true : v === '1';
  });
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const url = settings?.musicUrl || '';
  const enabled = !!settings?.musicEnabled && !!url;
  const volume = typeof settings?.musicVolume === 'number' ? settings.musicVolume : 0.35;
  const autoplay = settings?.musicAutoplay !== false;
  const title = settings?.musicTitle || 'Bhakti Music';

  // Attempt autoplay when url/enable changes
  useEffect(() => {
    if (!enabled || !audioRef.current) return;
    const a = audioRef.current;
    a.volume = Math.max(0, Math.min(1, volume));
    a.muted = muted;
    if (autoplay) {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, [enabled, url, autoplay, volume, muted]);

  // Smart mode: on the very first user interaction anywhere on the page,
  // auto-unmute the music so bhakti starts audible without any extra tap.
  useEffect(() => {
    if (!enabled) return;
    const a = audioRef.current;
    if (!a) return;
    let done = false;
    const unmuteOnce = async () => {
      if (done) return;
      done = true;
      try {
        a.muted = false;
        setMuted(false);
        if (a.paused) await a.play();
        setPlaying(true);
      } catch {
        // Ignore — user can still click the player button
      }
      window.removeEventListener('pointerdown', unmuteOnce);
      window.removeEventListener('keydown', unmuteOnce);
      window.removeEventListener('touchstart', unmuteOnce);
      window.removeEventListener('scroll', unmuteOnce);
    };
    // Only auto-unmute if the user hasn't explicitly muted before
    const storedPref = typeof window !== 'undefined' ? localStorage.getItem('nv_music_muted_pref') : null;
    if (storedPref === '1') return; // user opted out
    window.addEventListener('pointerdown', unmuteOnce, { once: true, passive: true });
    window.addEventListener('keydown', unmuteOnce, { once: true, passive: true });
    window.addEventListener('touchstart', unmuteOnce, { once: true, passive: true });
    window.addEventListener('scroll', unmuteOnce, { once: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', unmuteOnce);
      window.removeEventListener('keydown', unmuteOnce);
      window.removeEventListener('touchstart', unmuteOnce);
      window.removeEventListener('scroll', unmuteOnce);
    };
  }, [enabled, url]);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('nv_music_muted', muted ? '1' : '0');
  }, [muted]);

  if (!enabled) return null;

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      try { await a.play(); setPlaying(true); } catch {}
    }
  };

  const toggleMute = async () => {
    const a = audioRef.current;
    setMuted((m) => {
      const next = !m;
      // Remember the user's explicit choice so smart-mode doesn't
      // fight them next time.
      if (typeof window !== 'undefined') {
        localStorage.setItem('nv_music_muted_pref', next ? '1' : '0');
      }
      if (a) {
        a.muted = next;
        // If unmuting for the first time, also ensure playback
        if (!next && a.paused) a.play().then(() => setPlaying(true)).catch(() => {});
      }
      return next;
    });
  };

  return (
    <>
      <audio ref={audioRef} src={url} loop preload="metadata" />
      <div
        data-testid="site-music-player"
        className="fixed bottom-5 left-5 z-40 flex items-center gap-2 bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-full shadow-xl pl-3 pr-1.5 py-1.5 hover:shadow-2xl transition-shadow"
      >
        <button
          type="button" onClick={toggleMute} data-testid="music-mute-toggle"
          className="inline-flex items-center gap-1.5 text-sm font-semibold"
          aria-label={muted ? 'Unmute background music' : 'Mute background music'}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 animate-pulse" />}
          <span className="hidden sm:inline">{muted ? 'Play Bhakti' : title}</span>
        </button>
        <button
          type="button" onClick={togglePlay} data-testid="music-play-toggle"
          className="w-8 h-8 rounded-full bg-white text-orange-600 flex items-center justify-center hover:bg-orange-50"
          aria-label={playing ? 'Pause music' : 'Play music'}
        >
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
        </button>
      </div>
    </>
  );
}
